import {ActivityPeriodEntity} from "@ots-frontend/ots-data";
import {useMemo} from "react";
import {ActionIcon, Box, Modal, ModalProps, Table} from "@mantine/core";
import {IconDownload} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {DefaultText} from "../../text/DefaultText/DefaultText";
import {getFormattedDatabaseCode} from "../../text/FormattedDatabaseCode/FormattedDatabaseCode";
import { getFormattedDateTime } from "../../date/FormattedDateTime/FormattedDateTime";

export type ActivityPeriodFileTableProps = {
  activityPeriod: ActivityPeriodEntity
}

export const ActivityPeriodFileTable = (props: ActivityPeriodFileTableProps) => {
  const {activityPeriod} = props

  const {t} = useTranslation(["common"])

  const tableRows = useMemo(() => {
    const rows = []

    if (activityPeriod.activityPeriods) {
      for (const subActivityPeriod of activityPeriod.activityPeriods) {
        if (subActivityPeriod.activityPeriodReports) {
          rows.push(
            <tr key={subActivityPeriod.id}>
              <td colSpan={2}>
                <b>
                  <DefaultText value={subActivityPeriod.truckCompanyName}/>
                </b>
              </td>
            </tr>
          )

          for (const report of subActivityPeriod.activityPeriodReports) {
            rows.push(
              <tr key={report.id}>
                <td>
                  {report.name + ": " + getFormattedDatabaseCode(report.activityPeriodStatus, t) + " - " + getFormattedDateTime(report.createDttm)}
                </td>
                <td>
                  <ActionIcon component="a" href={report.url} target={"_blank"}>
                    <IconDownload size={16}/>
                  </ActionIcon>
                </td>
              </tr>
            )
          }
        }
      }
    } else if (activityPeriod.activityPeriodReports) {
      rows.push(
        <tr key="reports">
          <td colSpan={2}>
            <b>Reports</b>
          </td>
        </tr>
      )

      for (const report of activityPeriod.activityPeriodReports) {
        rows.push(
          <tr key={report.id}>
            <td>
              {report.name + ": " + getFormattedDatabaseCode(report.activityPeriodStatus, t) + " - " + getFormattedDateTime(report.createDttm)}
            </td>
            <td>
              <ActionIcon component="a" href={report.url} target={"_blank"}>
                <IconDownload size={16}/>
              </ActionIcon>
            </td>
          </tr>
        )
      }
    }

    if (activityPeriod.supplementals && activityPeriod.supplementals.length > 0) {
      rows.push(
        <tr key="supplementals">
          <td colSpan={2}>
            <b>Additional</b>
          </td>
        </tr>
      )

      for (const supplemental of activityPeriod.supplementals) {
        rows.push(
          <tr key={supplemental.id}>
            <td>
              {"Document: " + supplemental.displayName}
            </td>
            <td>
              <ActionIcon component="a" href={supplemental.url} target={"_blank"}>
                <IconDownload size={16}/>
              </ActionIcon>
            </td>
          </tr>
        )
      }
    }

    return rows
  }, [activityPeriod, t])

  return (
    <Table>
      <tbody>
      {tableRows}
      </tbody>
    </Table>
  )
}

export type ActivityPeriodFileTableModalProps = Omit<ModalProps, "onSubmit"> & ActivityPeriodFileTableProps

export const ActivityPeriodFileTableModal = (props: ActivityPeriodFileTableModalProps) => {
  const {t} = useTranslation(["common"]);

  const {
    title = t<string>("common.shared.file.plural", "Files"),
    activityPeriod,
    ...rest
  } = props;

  return (
    <Modal {...rest} title={title}>
      <Box>
        <ActivityPeriodFileTable
          activityPeriod={activityPeriod}/>
      </Box>
    </Modal>
  );
};
